import { useState } from 'react';
import { Brain, Info, Menu, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '../store/sessionStore';
import { useLanguage } from '../lib/i18n/context';
import { LanguageSelector } from './LanguageSelector';

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  const session = useSessionStore((state) => state.session);
  const participant = useSessionStore((state) => state.participant);
  const { translateUrl } = useLanguage();

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // If there's an active session and participant, navigate to the session
    if (session?.id && participant?.id && !session.is_finished) {
      navigate(`/session/${session.id}`);
    } else {
      // Otherwise, go to language-specific home
      navigate(`/${language}`);
    }
    setIsMenuOpen(false);
  };

  const handleNavigation = (path: string) => {
    navigate(translateUrl(path));
    setIsMenuOpen(false);
  };

  return (
    <nav className="relative bg-white shadow-sm z-50">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={handleLogoClick}
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
          >
            <Brain className="h-8 w-8 text-primary-500" />
            <span className="text-xl font-semibold text-gray-900">ABCbrain.xyz</span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link
              to={translateUrl('/de/die-ABC-Listen-Methode')}
              className="group relative inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-primary-500/10 to-accent-500/10 px-4 py-2 text-primary-700 hover:text-accent-700 transition-all duration-300 hover:shadow-lg hover:shadow-accent-500/10"
            >
              <Info className="h-5 w-5 transition-transform group-hover:rotate-12 group-hover:scale-110" />
              <span className="font-medium">{t('nav.about')}</span>
              <span className="absolute inset-0 rounded-full bg-gradient-to-r from-primary-500/20 via-accent-500/20 to-primary-500/20 opacity-0 group-hover:opacity-100 group-hover:animate-shimmer transition-opacity" />
            </Link>
            <Link
              to={translateUrl('/de/datenschutz')}
              className="text-gray-600 hover:text-primary-600 transition-colors"
            >
              {t('nav.privacy')}
            </Link>
            <Link
              to={translateUrl('/de/impressum')}
              className="text-gray-600 hover:text-primary-600 transition-colors"
            >
              {t('nav.imprint')}
            </Link>
            <LanguageSelector />
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6 text-gray-600" />
            ) : (
              <Menu className="h-6 w-6 text-gray-600" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`absolute inset-x-0 top-full bg-white shadow-lg transition-all duration-300 ease-in-out md:hidden ${
          isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'
        }`}
      >
        <div className="container mx-auto px-4 py-3 space-y-4">
          <button
            onClick={() => handleNavigation('/de/die-ABC-Listen-Methode')}
            className="w-full flex items-center gap-2 rounded-lg bg-gradient-to-r from-primary-500/10 to-accent-500/10 px-4 py-3 text-primary-700 hover:from-primary-500/20 hover:to-accent-500/20 transition-all"
          >
            <Info className="h-5 w-5 flex-shrink-0" />
            <span className="font-medium">{t('nav.about')}</span>
          </button>
          <div className="flex flex-col space-y-2 pb-2">
            <button
              onClick={() => handleNavigation('/de/datenschutz')}
              className="w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            >
              {t('nav.privacy')}
            </button>
            <button
              onClick={() => handleNavigation('/de/impressum')}
              className="w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            >
              {t('nav.imprint')}
            </button>
            <div className="px-4 py-2">
              <LanguageSelector />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}